import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import Flyout, { Panel, anchorPoints, AVAILABLE_ANCHOR_POINTS, offsetContainerVertical, offsetPanelHorizontal } from '@hig/flyout';
import IconButton from '@hig/icon-button';
import { List16, List24, Help16, Help24 } from '@hig/icons';
import ThemeContext from '@hig/theme-context';
import { combineEventHandlers, createCustomClassNames } from '@hig/utils';
import Typography from '@hig/typography';
import NotificationsFlyout, { anchorPoints as anchorPoints$1 } from '@hig/notifications-flyout';
import ProfileFlyout, { anchorPoints as anchorPoints$2, AVAILABLE_ANCHOR_POINTS as AVAILABLE_ANCHOR_POINTS$1, ProfileContent } from '@hig/profile-flyout';
export { ProfileContent } from '@hig/profile-flyout';

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function getTopNavThemeData(themeData) {
  return {
    background: themeData["colorScheme.surface.level100"],
    fontFamily: themeData["basics.fontFamilies.main"],
    boxShadow: "0 4px " + themeData["basics.shadows.lowBlur"] + " -4px rgba(0, 0, 0, 0.25)"
  };
}

function stylesheet(props, themeData) {
  var customStylesheet = props.stylesheet;

  var styles = {
    topNav: _extends({
      display: "flex",
      position: "relative",
      height: "56px",
      minHeight: "56px",
      paddingLeft: "7px",
      justifyContent: "space-between",
      alignItems: "stretch",
      zIndex: 9998,
      lineHeight: 1.6,
      boxSizing: "border-box"
    }, themeData ? getTopNavThemeData(themeData) : {}),
    topNavSpacer: {
      flex: "1 1 0"
    },
    topNavLogoWrapper: {
      display: "flex",
      padding: "0 12px 0 5px",
      alignItems: "center",
      lineHeight: 0
    },
    topNavSeparator: {
      width: 0,
      height: "32px",
      margin: "12px 6px",
      borderLeftWidth: "1px",
      borderLeftColor: themeData ? themeData["divider.heavyColor"] : "transparent",
      borderLeftStyle: "solid"
    },
    topNavProfileAction: {
      display: "flex",
      alignItems: "center",
      boxSizing: "border-box"
    },
    topNavProfileActionButtonWrapper: {
      display: "flex",
      alignItems: "center",
      padding: "0 6px"
    },
    topNavLogo: {
      display: "flex",
      textDecoration: "none",
      cursor: "pointer"
    },
    topNavLogoTextPresenter: {
      display: "inline-block",
      margin: 0,
      fontSize: "16px",
      lineHeight: "16px",
      fontWeight: 400
    },
    topNavInteractions: {
      display: "flex",
      alignItems: "center",
      padding: "0 6px"
    },
    topNavAction: {
      display: "flex",
      height: "56px",
      alignItems: "center"
    },
    topNavActionFlyoutPanel: {
      width: "276px",
      maxHeight: "360px",
      overflowY: "auto",
      msOverflowStyle: "-ms-autohiding-scrollbar"
    }
  };

  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function ActionPresenter(_ref) {
  var children = _ref.children,
      customStylesheet = _ref.stylesheet,
      otherProps = _objectWithoutProperties(_ref, ["children", "stylesheet"]);

  var className = otherProps.className;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});
  return React.createElement(
    "div",
    { className: cx([className, css(styles.topNavAction)]) },
    children
  );
}

ActionPresenter.propTypes = {
  /** Content to render inside an action */
  children: PropTypes.node,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
ActionPresenter.__docgenInfo = {
  "description": "",
  "displayName": "ActionPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Content to render inside an action"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

function ActionFlyoutPanelPresenter(_ref) {
  var children = _ref.children,
      handleScroll = _ref.handleScroll,
      innerRef = _ref.innerRef,
      customStylesheet = _ref.stylesheet;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});
  return React.createElement(
    Panel,
    { innerRef: innerRef },
    React.createElement(
      "div",
      {
        className: css(styles.topNavActionFlyoutPanel),
        onScroll: handleScroll
      },
      children
    )
  );
}

ActionFlyoutPanelPresenter.propTypes = {
  children: PropTypes.node,
  handleScroll: PropTypes.func,
  innerRef: PropTypes.func,
  stylesheet: PropTypes.func
};

/* eslint-disable-next-line react/prop-types, prettier/prettier */
function renderActionFlyoutPanel(_ref2) {
  var content = _ref2.content,
      innerRef = _ref2.innerRef,
      customStylesheet = _ref2.stylesheet;

  return React.createElement(
    ActionFlyoutPanelPresenter,
    {
      innerRef: innerRef,
      stylesheet: customStylesheet
    },
    content
  );
}

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function NavButtonPresenter(_ref) {
  var icon = _ref.icon,
      onClick = _ref.onClick,
      title = _ref.title,
      otherProps = _objectWithoutProperties$1(_ref, ["icon", "onClick", "title"]);

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref2) {
      var metadata = _ref2.metadata;

      var NavDefaultIcon = metadata.densityId === "high-density" ? List16 : List24;
      return React.createElement(IconButton, _extends$1({
        icon: icon || React.createElement(NavDefaultIcon, null),
        onClick: onClick,
        title: title
      }, otherProps));
    }
  );
}

NavButtonPresenter.defaultProps = {
  title: ""
};

NavButtonPresenter.propTypes = {
  icon: PropTypes.element,
  onClick: PropTypes.func,
  title: PropTypes.string
};
NavButtonPresenter.__docgenInfo = {
  "description": "",
  "displayName": "NavButtonPresenter",
  "props": {
    "icon": {
      "type": {
        "name": "element"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      }
    }
  }
};

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$2(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/** @typedef {import("@hig/flyout").Coordinates} Coordinates */

var NavAction = function (_Component) {
  _inherits(NavAction, _Component);

  function NavAction() {
    _classCallCheck(this, NavAction);

    return _possibleConstructorReturn(this, (NavAction.__proto__ || Object.getPrototypeOf(NavAction)).apply(this, arguments));
  }

  _createClass(NavAction, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          alterCoordinates = _props.alterCoordinates,
          anchorPoint = _props.anchorPoint,
          children = _props.children,
          fallbackAnchorPoints = _props.fallbackAnchorPoints,
          icon = _props.icon,
          onClick = _props.onClick,
          stylesheet = _props.stylesheet,
          title = _props.title,
          otherProps = _objectWithoutProperties$2(_props, ["alterCoordinates", "anchorPoint", "children", "fallbackAnchorPoints", "icon", "onClick", "stylesheet", "title"]);

      var className = otherProps.className;

      var topNavNavActionFlyoutClassName = createCustomClassNames(className, "top-nav__nav-action-flyout");
      var topNavNavActionButtonClassName = createCustomClassNames(className, "top-nav__nav-action-button");

      return React.createElement(
        ActionPresenter,
        { className: className, stylesheet: stylesheet },
        React.createElement(
          Flyout,
          {
            alterCoordinates: alterCoordinates,
            anchorPoint: anchorPoint,
            className: topNavNavActionFlyoutClassName,
            content: children,
            fallbackAnchorPoints: fallbackAnchorPoints,
            onClick: onClick,
            panel: renderActionFlyoutPanel,
            stylesheet: stylesheet
          },
          function (_ref) {
            var handleClick = _ref.handleClick;
            return React.createElement(NavButtonPresenter, {
              className: topNavNavActionButtonClassName,
              icon: icon,
              onClick: combineEventHandlers(onClick, handleClick),
              title: title
            });
          }
        )
      );
    }
  }]);

  return NavAction;
}(Component);

NavAction.propTypes = {
  /** Manipulate flyout coordinates before each render */
  alterCoordinates: PropTypes.func,
  /** Where the flyout will be anchored relative to target */
  anchorPoint: PropTypes.string,
  /** Content to render inside the pull down flyout */
  children: PropTypes.node,
  /**
   * When the flyout overflows the viewport, it'll attempt to
   * use the given anchor points in order to keep the flyout
   * within the viewport
   */
  fallbackAnchorPoints: PropTypes.arrayOf(PropTypes.oneOf(AVAILABLE_ANCHOR_POINTS)),
  /** Icon to be displayed in the top bar for the pull down, default is List */
  icon: PropTypes.element,
  /** Callback when the flyout is opened */
  onClick: PropTypes.func,
  /** Title text displayed in the tooltip on button hover */
  title: PropTypes.string,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
NavAction.defaultProps = {
  /**
   * @param {Coordinates} coordinates
   * @returns {Coordinates}
   */
  alterCoordinates: function alterCoordinates(coordinates) {
    return offsetPanelHorizontal(offsetContainerVertical(coordinates, 8), 57);
  },

  anchorPoint: anchorPoints.TOP_RIGHT,
  fallbackAnchorPoints: []
};
NavAction.__docgenInfo = {
  "description": "@typedef {import(\"@hig/flyout\").Coordinates} Coordinates",
  "displayName": "NavAction",
  "props": {
    "alterCoordinates": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Manipulate flyout coordinates before each render",
      "defaultValue": {
        "value": "function(coordinates) {\n  return offsetPanelHorizontal(offsetContainerVertical(coordinates, 8), 57);\n}",
        "computed": false
      }
    },
    "anchorPoint": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Where the flyout will be anchored relative to target",
      "defaultValue": {
        "value": "anchorPoints.TOP_RIGHT",
        "computed": true
      }
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Content to render inside the pull down flyout"
    },
    "fallbackAnchorPoints": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "enum",
          "computed": true,
          "value": "AVAILABLE_ANCHOR_POINTS"
        }
      },
      "required": false,
      "description": "When the flyout overflows the viewport, it'll attempt to\nuse the given anchor points in order to keep the flyout\nwithin the viewport",
      "defaultValue": {
        "value": "[]",
        "computed": false
      }
    },
    "icon": {
      "type": {
        "name": "element"
      },
      "required": false,
      "description": "Icon to be displayed in the top bar for the pull down, default is List"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Callback when the flyout is opened"
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Title text displayed in the tooltip on button hover"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$3(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/** @typedef {import("@hig/flyout").Coordinates} Coordinates */

var HelpAction = function (_Component) {
  _inherits$1(HelpAction, _Component);

  function HelpAction() {
    _classCallCheck$1(this, HelpAction);

    return _possibleConstructorReturn$1(this, (HelpAction.__proto__ || Object.getPrototypeOf(HelpAction)).apply(this, arguments));
  }

  _createClass$1(HelpAction, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          alterCoordinates = _props.alterCoordinates,
          anchorPoint = _props.anchorPoint,
          children = _props.children,
          fallbackAnchorPoints = _props.fallbackAnchorPoints,
          onClick = _props.onClick,
          stylesheet = _props.stylesheet,
          otherProps = _objectWithoutProperties$3(_props, ["alterCoordinates", "anchorPoint", "children", "fallbackAnchorPoints", "onClick", "stylesheet"]);

      var className = otherProps.className;


      return React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref) {
          var metadata = _ref.metadata;

          var title = "View help";
          var HelpIcon = metadata.densityId === "high-density" ? Help16 : Help24;
          return React.createElement(
            NavAction,
            {
              alterCoordinates: alterCoordinates,
              anchorPoint: anchorPoint,
              className: className,
              fallbackAnchorPoints: fallbackAnchorPoints,
              icon: React.createElement(HelpIcon, null),
              onClick: onClick,
              stylesheet: stylesheet,
              title: title
            },
            children
          );
        }
      );
    }
  }]);

  return HelpAction;
}(Component);

HelpAction.propTypes = {
  /** Manipulate flyout coordinates before each render */
  alterCoordinates: PropTypes.func,
  /** Where the flyout will be anchored relative to target */
  anchorPoint: PropTypes.string,
  /** Content to render inside the help flyout */
  children: PropTypes.node,
  /**
   * When the flyout overflows the viewport, it'll attempt to
   * use the given anchor points in order to keep the flyout
   * within the viewport
   */
  fallbackAnchorPoints: PropTypes.arrayOf(PropTypes.oneOf(AVAILABLE_ANCHOR_POINTS)),
  /** Callback when the flyout is opened */
  onClick: PropTypes.func,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
HelpAction.defaultProps = {
  /**
   * @param {Coordinates} coordinates
   * @returns {Coordinates}
   */
  alterCoordinates: function alterCoordinates(coordinates) {
    return offsetPanelHorizontal(offsetContainerVertical(coordinates, 8), 57);
  },

  anchorPoint: anchorPoints.TOP_RIGHT,
  fallbackAnchorPoints: []
};
HelpAction.__docgenInfo = {
  "description": "@typedef {import(\"@hig/flyout\").Coordinates} Coordinates",
  "displayName": "HelpAction",
  "props": {
    "alterCoordinates": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Manipulate flyout coordinates before each render",
      "defaultValue": {
        "value": "function(coordinates) {\n  return offsetPanelHorizontal(offsetContainerVertical(coordinates, 8), 57);\n}",
        "computed": false
      }
    },
    "anchorPoint": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Where the flyout will be anchored relative to target",
      "defaultValue": {
        "value": "anchorPoints.TOP_RIGHT",
        "computed": true
      }
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Content to render inside the help flyout"
    },
    "fallbackAnchorPoints": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "enum",
          "computed": true,
          "value": "AVAILABLE_ANCHOR_POINTS"
        }
      },
      "required": false,
      "description": "When the flyout overflows the viewport, it'll attempt to\nuse the given anchor points in order to keep the flyout\nwithin the viewport",
      "defaultValue": {
        "value": "[]",
        "computed": false
      }
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Callback when the flyout is opened"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

function _objectWithoutProperties$4(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function InteractionsPresenter(_ref) {
  var children = _ref.children,
      innerRef = _ref.innerRef,
      customStylesheet = _ref.stylesheet,
      otherProps = _objectWithoutProperties$4(_ref, ["children", "innerRef", "stylesheet"]);

  var className = otherProps.className;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});
  return React.createElement(
    "div",
    {
      className: cx([className, css(styles.topNavInteractions)]),
      ref: innerRef
    },
    children
  );
}

InteractionsPresenter.propTypes = {
  /** Actions to be rendered */
  children: PropTypes.node,
  /** Reference the wrappinf <div /> element */
  innerRef: PropTypes.func,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
InteractionsPresenter.__docgenInfo = {
  "description": "",
  "displayName": "InteractionsPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Actions to be rendered"
    },
    "innerRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Reference the wrappinf <div /> element"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$2 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$5(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$2(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$2(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var Interactions = function (_Component) {
  _inherits$2(Interactions, _Component);

  function Interactions() {
    _classCallCheck$2(this, Interactions);

    return _possibleConstructorReturn$2(this, (Interactions.__proto__ || Object.getPrototypeOf(Interactions)).apply(this, arguments));
  }

  _createClass$2(Interactions, [{
    key: "renderChildren",


    /**
     * @todo Complete render prop implementation with alignment helpers
     */
    value: function renderChildren() {
      var children = this.props.children;


      if (typeof children === "function") {
        return children();
      }

      return children;
    }
  }, {
    key: "render",
    value: function render() {
      var _props = this.props,
          stylesheet = _props.stylesheet,
          otherProps = _objectWithoutProperties$5(_props, ["stylesheet"]);

      return React.createElement(
        InteractionsPresenter,
        _extends$2({ stylesheet: stylesheet }, otherProps),
        this.renderChildren()
      );
    }
  }]);

  return Interactions;
}(Component);

Interactions.propTypes = {
  /** Actions to be rendered */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
Interactions.__docgenInfo = {
  "description": "",
  "displayName": "Interactions",
  "props": {
    "children": {
      "type": {
        "name": "union",
        "value": [{
          "name": "node"
        }, {
          "name": "func"
        }]
      },
      "required": false,
      "description": "Actions to be rendered"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

function LogoTextPresenter(_ref) {
  var children = _ref.children,
      customStylesheet = _ref.stylesheet;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});
  return React.createElement(
    Typography,
    { variant: "h1", style: styles.topNavLogoTextPresenter },
    children
  );
}

LogoTextPresenter.propTypes = {
  /** Logo content */
  children: PropTypes.node,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
LogoTextPresenter.__docgenInfo = {
  "description": "",
  "displayName": "LogoTextPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Logo content"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

function _objectWithoutProperties$6(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function renderChildren(children, customStylesheet) {
  if (typeof children === "string") {
    return React.createElement(
      LogoTextPresenter,
      { stylesheet: customStylesheet },
      children
    );
  }

  return children;
}

function LogoPresenter(_ref) {
  var label = _ref.label,
      link = _ref.link,
      title = _ref.title,
      onClick = _ref.onClick,
      children = _ref.children,
      dangerouslySetInnerHTML = _ref.dangerouslySetInnerHTML,
      customStylesheet = _ref.stylesheet,
      otherProps = _objectWithoutProperties$6(_ref, ["label", "link", "title", "onClick", "children", "dangerouslySetInnerHTML", "stylesheet"]);

  var Wrapper = link ? "a" : "div";
  var className = otherProps.className;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});

  return React.createElement(
    Wrapper,
    {
      className: cx([className, css(styles.topNavLogo)]),
      href: link,
      title: title,
      "aria-label": label,
      onClick: onClick,
      dangerouslySetInnerHTML: dangerouslySetInnerHTML
    },
    renderChildren(children, customStylesheet)
  );
}

LogoPresenter.propTypes = {
  /** A11y label */
  label: PropTypes.string,
  /** URL to link to */
  link: PropTypes.string,
  /** A11y title */
  title: PropTypes.string,
  /** Click event listener */
  onClick: PropTypes.func,
  /** Logo content */
  children: PropTypes.node,
  /** Proxy for React's `dangerouslySetInnerHTML` */
  // eslint-disable-next-line react/forbid-prop-types
  dangerouslySetInnerHTML: PropTypes.any,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
LogoPresenter.__docgenInfo = {
  "description": "",
  "displayName": "LogoPresenter",
  "props": {
    "label": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "A11y label"
    },
    "link": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "URL to link to"
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "A11y title"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Click event listener"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Logo content"
    },
    "dangerouslySetInnerHTML": {
      "type": {
        "name": "any"
      },
      "required": false,
      "description": "Proxy for React's `dangerouslySetInnerHTML`"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

var _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$3 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$7(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$3(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$3(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$3(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/** @typedef {import("@hig/flyout").Coordinates} Coordinates */

var NotificationsAction = function (_Component) {
  _inherits$3(NotificationsAction, _Component);

  function NotificationsAction() {
    _classCallCheck$3(this, NotificationsAction);

    return _possibleConstructorReturn$3(this, (NotificationsAction.__proto__ || Object.getPrototypeOf(NotificationsAction)).apply(this, arguments));
  }

  _createClass$3(NotificationsAction, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          children = _props.children,
          otherProps = _objectWithoutProperties$7(_props, ["children"]);

      var className = otherProps.className,
          stylesheet = otherProps.stylesheet;

      var topNavNotificationsFlyoutClassName = createCustomClassNames(className, "top-nav__notifications-flyout");

      return React.createElement(
        ActionPresenter,
        { stylesheet: stylesheet, className: className },
        React.createElement(
          NotificationsFlyout,
          _extends$3({}, otherProps, {
            stylesheet: stylesheet,
            className: topNavNotificationsFlyoutClassName
          }),
          children
        )
      );
    }
  }]);

  return NotificationsAction;
}(Component);

NotificationsAction.propTypes = NotificationsFlyout.propTypes;
NotificationsAction.defaultProps = {
  /**
   * @param {Coordinates} coordinates
   * @returns {Coordinates}
   */
  alterCoordinates: function alterCoordinates(coordinates) {
    return offsetPanelHorizontal(offsetContainerVertical(coordinates, 8), 93);
  },

  anchorPoint: anchorPoints$1.TOP_RIGHT,
  fallbackAnchorPoints: []
};
NotificationsAction.__docgenInfo = {
  "description": "@typedef {import(\"@hig/flyout\").Coordinates} Coordinates",
  "displayName": "NotificationsAction",
  "props": {
    "alterCoordinates": {
      "defaultValue": {
        "value": "function(coordinates) {\n  return offsetPanelHorizontal(offsetContainerVertical(coordinates, 8), 93);\n}",
        "computed": false
      },
      "required": false
    },
    "anchorPoint": {
      "defaultValue": {
        "value": "anchorPoints.TOP_RIGHT",
        "computed": true
      },
      "required": false
    },
    "fallbackAnchorPoints": {
      "defaultValue": {
        "value": "[]",
        "computed": false
      },
      "required": false
    }
  },
  "composes": ["@hig/notifications-flyout"]
};

function SeparatorPresenter(_ref) {
  var customStylesheet = _ref.stylesheet;

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref2) {
      var resolvedRoles = _ref2.resolvedRoles;

      var styles = stylesheet({ stylesheet: customStylesheet }, resolvedRoles);
      return React.createElement("div", {
        role: "presentation",
        "aria-hidden": true,
        className: css(styles.topNavSeparator)
      });
    }
  );
}

SeparatorPresenter.propTypes = {
  stylesheet: PropTypes.func
};
SeparatorPresenter.__docgenInfo = {
  "description": "",
  "displayName": "SeparatorPresenter",
  "props": {
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

function _objectWithoutProperties$8(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

/**
 * @todo Remove the <SeparatorPresenter /> and wrapping <div /> component
 */
function ProfileActionPresenter(_ref) {
  var children = _ref.children,
      customStylesheet = _ref.stylesheet,
      otherProps = _objectWithoutProperties$8(_ref, ["children", "stylesheet"]);

  var className = otherProps.className;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});
  var topNavProfileActionButtonWrapperClassName = createCustomClassNames(className, "top-nav-profile-action-button-wrapper");

  return React.createElement(
    "div",
    { className: css(styles.topNavProfileAction) },
    React.createElement(SeparatorPresenter, null),
    React.createElement(
      ActionPresenter,
      { className: className, stylesheet: customStylesheet },
      React.createElement(
        "div",
        {
          className: cx([topNavProfileActionButtonWrapperClassName, css(styles.topNavProfileActionButtonWrapper)])
        },
        children
      )
    )
  );
}

ProfileActionPresenter.propTypes = {
  children: PropTypes.node,
  stylesheet: PropTypes.func
};
ProfileActionPresenter.__docgenInfo = {
  "description": "@todo Remove the <SeparatorPresenter /> and wrapping <div /> component",
  "displayName": "ProfileActionPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

var _createClass$4 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$9(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$4(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$4(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$4(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

/** @typedef {import("@hig/flyout").Coordinates} Coordinates */

var ProfileAction = function (_Component) {
  _inherits$4(ProfileAction, _Component);

  function ProfileAction() {
    _classCallCheck$4(this, ProfileAction);

    return _possibleConstructorReturn$4(this, (ProfileAction.__proto__ || Object.getPrototypeOf(ProfileAction)).apply(this, arguments));
  }

  _createClass$4(ProfileAction, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          alterCoordinates = _props.alterCoordinates,
          anchorPoint = _props.anchorPoint,
          avatarImage = _props.avatarImage,
          avatarName = _props.avatarName,
          children = _props.children,
          fallbackAnchorPoints = _props.fallbackAnchorPoints,
          onClick = _props.onClick,
          stylesheet = _props.stylesheet,
          otherProps = _objectWithoutProperties$9(_props, ["alterCoordinates", "anchorPoint", "avatarImage", "avatarName", "children", "fallbackAnchorPoints", "onClick", "stylesheet"]);

      var className = otherProps.className;

      var topNavProfileFlyoutClassName = createCustomClassNames(className, "top-nav__profile-flyout");

      return React.createElement(
        ProfileActionPresenter,
        { className: className },
        React.createElement(
          ProfileFlyout,
          {
            alterCoordinates: alterCoordinates,
            anchorPoint: anchorPoint,
            avatarImage: avatarImage,
            avatarName: avatarName,
            className: topNavProfileFlyoutClassName,
            fallbackAnchorPoints: fallbackAnchorPoints,
            onProfileImageClick: onClick,
            panel: renderActionFlyoutPanel,
            stylesheet: stylesheet
          },
          children
        )
      );
    }
  }]);

  return ProfileAction;
}(Component);

ProfileAction.propTypes = {
  /** Manipulate flyout coordinates before each render */
  alterCoordinates: PropTypes.func,
  /** Where the flyout will be anchored relative to target */
  anchorPoint: PropTypes.string,
  /** The name that will converted into initials, and displayed when an image isn't available */
  avatarName: PropTypes.string.isRequired,
  /** Url to a profile image */
  avatarImage: PropTypes.string,
  /**
   * When the flyout overflows the viewport, it'll attempt to
   * use the given anchor points in order to keep the flyout
   * within the viewport.
   */
  fallbackAnchorPoints: PropTypes.arrayOf(PropTypes.oneOf(AVAILABLE_ANCHOR_POINTS$1)),
  /** Content to render in the profile flyout */
  children: PropTypes.node,
  /** Callback when the flyout is opened */
  onClick: PropTypes.func,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
ProfileAction.defaultProps = {
  /**
   * @param {Coordinates} coordinates
   * @returns {Coordinates}
   */
  alterCoordinates: function alterCoordinates(coordinates) {
    return offsetPanelHorizontal(offsetContainerVertical(coordinates, 10), 6);
  },

  anchorPoint: anchorPoints$2.TOP_RIGHT,
  fallbackAnchorPoints: []
};
ProfileAction.__docgenInfo = {
  "description": "@typedef {import(\"@hig/flyout\").Coordinates} Coordinates",
  "displayName": "ProfileAction",
  "props": {
    "alterCoordinates": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Manipulate flyout coordinates before each render",
      "defaultValue": {
        "value": "function(coordinates) {\n  return offsetPanelHorizontal(offsetContainerVertical(coordinates, 10), 6);\n}",
        "computed": false
      }
    },
    "anchorPoint": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Where the flyout will be anchored relative to target",
      "defaultValue": {
        "value": "anchorPoints.TOP_RIGHT",
        "computed": true
      }
    },
    "avatarName": {
      "type": {
        "name": "string"
      },
      "required": true,
      "description": "The name that will converted into initials, and displayed when an image isn't available"
    },
    "avatarImage": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Url to a profile image"
    },
    "fallbackAnchorPoints": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "enum",
          "computed": true,
          "value": "AVAILABLE_ANCHOR_POINTS"
        }
      },
      "required": false,
      "description": "When the flyout overflows the viewport, it'll attempt to\nuse the given anchor points in order to keep the flyout\nwithin the viewport.",
      "defaultValue": {
        "value": "[]",
        "computed": false
      }
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Content to render in the profile flyout"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Callback when the flyout is opened"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

function _objectWithoutProperties$10(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function TopNav(_ref) {
  var leftActions = _ref.leftActions,
      rightActions = _ref.rightActions,
      logo = _ref.logo,
      customStylesheet = _ref.stylesheet,
      otherProps = _objectWithoutProperties$10(_ref, ["leftActions", "rightActions", "logo", "stylesheet"]);

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref2) {
      var resolvedRoles = _ref2.resolvedRoles;
      var className = otherProps.className;

      var styles = stylesheet({ stylesheet: customStylesheet }, resolvedRoles);
      var topNavLogoWrapperClassName = createCustomClassNames(className, "top-nav-logo-wrapper");
      var topNavSpacerClassName = createCustomClassNames(className, "top-nav-spacer");
      return React.createElement(
        "div",
        { className: cx([className, css(styles.topNav)]) },
        leftActions,
        React.createElement(
          "div",
          {
            className: cx([topNavLogoWrapperClassName, css(styles.topNavLogoWrapper)])
          },
          logo
        ),
        React.createElement("div", {
          role: "presentation",
          "aria-hidden": true,
          className: cx([topNavSpacerClassName, css(styles.topNavSpacer)])
        }),
        rightActions
      );
    }
  );
}

TopNav.propTypes = {
  /** Actions to render to the left of the logo */
  leftActions: PropTypes.node,
  /** Actions to render on the right of the bar */
  rightActions: PropTypes.node,
  /** Content to render as the logo */
  logo: PropTypes.node,
  /** Function to modify the component's styles */
  stylesheet: PropTypes.func
};
TopNav.__docgenInfo = {
  "description": "",
  "displayName": "TopNav",
  "props": {
    "leftActions": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Actions to render to the left of the logo"
    },
    "rightActions": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Actions to render on the right of the bar"
    },
    "logo": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Content to render as the logo"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

TopNav.Action = ActionPresenter;
TopNav.Interactions = Interactions;
TopNav.Logo = LogoPresenter;
TopNav.LogoText = LogoTextPresenter;
TopNav.Separator = SeparatorPresenter;

/**
 * @todo Remove the static properties below
 * Adding components as static property creates a convenient API,
 * but at the cost of breaking tree-shaking.
 *
 * While `HelpAction` and `ProfileAction` are relatively lightweight,
 * `NotificationsAction` is quite heavy due to its dependance upon `@hig/notifications-flyout`.
 * `NotificationsAction` shouldn't be added as a static property,
 * and for the sake of consistency neither should HelpAction` and `ProfileAction`.
 */
TopNav.HelpAction = HelpAction;
TopNav.ProfileContent = ProfileContent;
TopNav.ProfileAction = ProfileAction;

export default TopNav;
export { HelpAction, Interactions, NotificationsAction, ProfileAction, NavAction, ActionPresenter as Action, LogoPresenter as Logo, LogoTextPresenter as LogoText, SeparatorPresenter as Separator };
